import {
  BotMessage,
  ClientAttachment,
  InProgressMessage,
  Message,
  PersistentMessage,
} from "@/types/chat"
import { Feedback, FeedbackValue } from "@/types/feedback"
import { Place, PlaceType } from "@/types/place"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { getEmptyPlaceArray } from "@/lib/place-utils"
import { freeze } from "@/lib/utils/collections"
import { filterOutlierEntities } from "../chat-utils"
import { setViewSize } from "./app-slice"
import { addPendingBotResponse } from "./presence-slice"
import { RootState } from "./redux-store"
import userSlice, {
  selectInProgressMessage,
  selectPersistentMessages,
  selectUserPermissions,
} from "./user-slice"

// Chat-component modes (exposed/collapsed, scroll, attachments, etc.).
// Lives in Redux so changes don't redraw the whole <Chat> tree. Map hover
// state — used outside chat too — lives in map-hover-slice.

export type FilteredPlaceType = PlaceType | "added" | "saved" | undefined
export type HighlightedMessage =
  | PersistentMessage
  | InProgressMessage
  | undefined
export type ProcessCommissionableStatus =
  | "need-process"
  | "start-processing"
  | "process-done"

export interface ChatModeSliceState {
  showSmallUIMap: boolean
  expandedMap: boolean
  highlightedMessageId: Message["id"] | undefined
  filteredPlaceType: FilteredPlaceType
  feedback: {
    tentativeFeedback: Feedback | null
    confirmedFeedback: Record<string, FeedbackValue>
  }
  chatInputHasValue: boolean
  chatInputFocused: boolean
  attachments: ClientAttachment[]
  loadingAttachments: {
    [key: string]: boolean
  }
  filteredPlaces: Place[]
  editingMessageId: number | null
  shouldAutoScroll: boolean
  isScrolledToBottom: boolean
  isScrolledUp: boolean
  contentScrollable: boolean
  processCommissionableStatus: ProcessCommissionableStatus | undefined
  showChatGroupTooltip: boolean
}

export const chatModeSliceInitialState = freeze<ChatModeSliceState>({
  showSmallUIMap: false,
  expandedMap: false,
  highlightedMessageId: undefined,
  filteredPlaceType: undefined,
  feedback: {
    tentativeFeedback: null,
    confirmedFeedback: {},
  },
  chatInputHasValue: false,
  chatInputFocused: false,
  attachments: [],
  loadingAttachments: {},
  filteredPlaces: [],
  editingMessageId: null,
  shouldAutoScroll: true,
  isScrolledToBottom: true,
  isScrolledUp: false,
  contentScrollable: false,
  processCommissionableStatus: undefined,
  showChatGroupTooltip: false,
})

const chatModeSlice = createSlice({
  name: "chat",
  initialState: chatModeSliceInitialState,
  reducers: {
    setShowSmallUIMap: (state, action: PayloadAction<boolean>) => {
      state.showSmallUIMap = action.payload
    },
    setExpandedMap: (state, action: PayloadAction<boolean>) => {
      state.expandedMap = action.payload
    },
    setFilteredPlaceType: (state, action: PayloadAction<FilteredPlaceType>) => {
      state.filteredPlaceType = action.payload
    },
    setHighlightedMessage: (
      state,
      action: PayloadAction<HighlightedMessage>
    ) => {
      state.highlightedMessageId = action.payload?.id
    },
    resetHighlightedMessageIfInProgress: (
      state,
      action: PayloadAction<BotMessage>
    ) => {
      if (state.highlightedMessageId === "in-progress") {
        state.highlightedMessageId = action.payload.id
      }
    },
    setTentativeFeedback: (state, action: PayloadAction<Feedback | null>) => {
      state.feedback.tentativeFeedback = action.payload
    },
    setConfirmedFeedback: (
      state,
      action: PayloadAction<{
        key: string
        value: FeedbackValue
      }>
    ) => {
      const { key, value } = action.payload
      state.feedback.confirmedFeedback[key] = value
    },
    setChatInputHasValue: (state, action: PayloadAction<boolean>) => {
      state.chatInputHasValue = action.payload
    },
    setChatInputFocused: (state, action: PayloadAction<boolean>) => {
      state.chatInputFocused = action.payload
    },
    addAttachment: (state, action: PayloadAction<ClientAttachment>) => {
      state.attachments.push(action.payload)
    },
    updateAttachment: (state, action: PayloadAction<ClientAttachment>) => {
      const index = state.attachments.findIndex(
        (a) => a.id === action.payload.id
      )
      if (index !== -1) {
        state.attachments[index] = action.payload
      }
    },
    removeAttachment: (state, action: PayloadAction<string>) => {
      state.attachments = state.attachments.filter(
        (attachment) => attachment.id !== action.payload
      )
    },
    setAttachmentLoading: (
      state,
      action: PayloadAction<{ id: string; loading: boolean }>
    ) => {
      const attachment = state.attachments.find(
        (a) => a.id === action.payload.id
      )
      if (attachment) {
        attachment.loading = action.payload.loading
      }
    },
    clearAttachments: (state) => {
      state.attachments = []
    },
    setFilteredPlaces: (state, action: PayloadAction<Place[]>) => {
      state.filteredPlaces = action.payload
    },
    setEditingMessageId: (state, action: PayloadAction<number | null>) => {
      state.editingMessageId = action.payload
    },
    setShouldAutoScroll: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoScroll = action.payload
    },
    setIsScrolledToBottom: (state, action: PayloadAction<boolean>) => {
      state.isScrolledToBottom = action.payload
    },
    setIsScrolledUp: (state, action: PayloadAction<boolean>) => {
      state.isScrolledUp = action.payload
    },
    setContentScrollable: (state, action: PayloadAction<boolean>) => {
      state.contentScrollable = action.payload
    },
    setProcessCommissionableStatus: (
      state,
      action: PayloadAction<ProcessCommissionableStatus>
    ) => {
      state.processCommissionableStatus = action.payload
    },
    setShowChatGroupTooltip: (state, action: PayloadAction<boolean>) => {
      state.showChatGroupTooltip = action.payload
    },
  },
  // Listen for actions that were dispatched to other slices and
  // take related action here in the chat mode slice.
  extraReducers: (builder) => {
    // When message streaming stops, if we were focused on the in-progress
    // message, focus on the final message instead.
    builder
      .addCase(userSlice.actions.stopMessageStreaming, (state, action) => {
        if (state.highlightedMessageId === "in-progress") {
          state.highlightedMessageId = action.payload.finalMessage.id
        }
        // Do we need to process commissionable?
        if (state.processCommissionableStatus === "need-process") {
          // Now call to perform the commissionable for chat
          state.processCommissionableStatus = "start-processing"
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingWithError, (state) => {
        if (state.highlightedMessageId === "in-progress") {
          state.highlightedMessageId = undefined
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingAndDisable, (state) => {
        if (state.highlightedMessageId === "in-progress") {
          state.highlightedMessageId = undefined
        }
      })
      .addCase(userSlice.actions.stopMessageStreamingWithCancel, (state) => {
        if (state.highlightedMessageId === "in-progress") {
          state.highlightedMessageId = undefined
        }
      })
      .addCase(setViewSize, (state) => {
        // Crossing a breakpoint re-creates the chat form (mobile portals it
        // into the BottomStack), so the textarea it leaves behind never fires
        // the blur React is listening for. Drop the flag here or the composer
        // stays latched in its keyboard-up layout at the new size.
        state.chatInputFocused = false
      })
      .addCase(userSlice.actions.setChat, (state, action) => {
        state.showChatGroupTooltip = action.payload.group
      })
      // The "Mindtrip is responding" indicator sits where the tooltip does
      .addCase(addPendingBotResponse, (state) => {
        state.showChatGroupTooltip = false
      })
      .addCase(userSlice.actions.resetChat, () => chatModeSliceInitialState)
  },
})

const selectSliceState = (state: RootState) => state.chatModeSlice

export const selectShowSmallUIMap = createSelector(
  [selectSliceState],
  (state) => state.showSmallUIMap
)

export const selectExpandedMap = createSelector(
  [selectSliceState],
  (state) => state.expandedMap
)

export const selectTentativeFeedback = createSelector(
  [selectSliceState],
  (state) => state.feedback.tentativeFeedback
)

export const selectConfirmedFeedback =
  (key: string) =>
  (state: RootState): FeedbackValue | undefined => {
    return state.chatModeSlice.feedback.confirmedFeedback[key]
  }

export const selectHighlightedMessageId = createSelector(
  [selectSliceState],
  (state) => state.highlightedMessageId
)

export const selectHasHighlightedMessage = createSelector(
  [selectHighlightedMessageId],
  (highlightedMessageId) => highlightedMessageId !== undefined
)

export const selectHighlightedMessage = createSelector(
  [
    selectPersistentMessages,
    selectInProgressMessage,
    selectHighlightedMessageId,
  ],
  (persistentMessages, inProgressMessage, id) => {
    if (id === "in-progress") {
      return inProgressMessage
    } else {
      return persistentMessages.find((message) => message.id === id)
    }
  }
)

export const selectHighlightedPlaces = createSelector(
  [selectHighlightedMessage],
  (message) => {
    if (!message) {
      // Return a constant empty array instead of returning [],
      // because [] makes a new empty array every time we get to it,
      // which defeats memoization and leads to unnecessary re-renders.
      return getEmptyPlaceArray()
    } else {
      let entities = filterOutlierEntities(message)
      if (entities.length === 0) {
        entities = message.places
      }
      return entities || getEmptyPlaceArray()
    }
  }
)

export const selectFilteredPlaceType = createSelector(
  [selectSliceState],
  (state) => state.filteredPlaceType
)

export const selectEditingMessageId = (state: RootState) =>
  state.chatModeSlice.editingMessageId

// permissions
export const selectCanShareTripPermission = createSelector(
  [selectUserPermissions],
  (permissions) => !!permissions?.includes("can_share_trip")
)

export const selectChatInputHasValue = createSelector(
  [selectSliceState],
  (state) => state.chatInputHasValue
)

export const selectChatInputFocused = createSelector(
  [selectSliceState],
  (state) => state.chatInputFocused
)

export const selectAttachments = createSelector(
  [selectSliceState],
  (state) => state.attachments
)

export const selectHasLoadingAttachments = createSelector(
  [selectAttachments],
  (attachments) => attachments.some((attachment) => attachment.loading)
)

export const selectAttachmentsCount = createSelector(
  [selectAttachments],
  (attachments) => attachments.length
)

export const selectShouldAutoScroll = createSelector(
  [selectSliceState],
  (state) => state.shouldAutoScroll
)

export const selectIsScrolledToBottom = createSelector(
  [selectSliceState],
  (state) => state.isScrolledToBottom
)

export const selectIsScrolledUp = createSelector(
  [selectSliceState],
  (state) => state.isScrolledUp
)

export const selectContentScrollable = createSelector(
  [selectSliceState],
  (state) => state.contentScrollable
)

export const selectProcessCommissionableStatus = createSelector(
  [selectSliceState],
  (state) => state.processCommissionableStatus
)

export const selectShowChatGroupTooltip = createSelector(
  [selectSliceState],
  (state) => state.showChatGroupTooltip
)

export const {
  setShowSmallUIMap,
  setExpandedMap,
  setFilteredPlaceType,
  setHighlightedMessage,
  setTentativeFeedback,
  setConfirmedFeedback,
  setChatInputHasValue,
  setChatInputFocused,
  addAttachment,
  updateAttachment,
  removeAttachment,
  clearAttachments,
  setAttachmentLoading,
  setFilteredPlaces,
  setEditingMessageId,
  setShouldAutoScroll,
  setIsScrolledToBottom,
  setIsScrolledUp,
  setContentScrollable,
  setShowChatGroupTooltip,
} = chatModeSlice.actions

export type ChatModeSlice = typeof chatModeSlice
export default chatModeSlice
